
import * as L from 'leaflet';
import moment from 'moment';
import { LeafletDirective, LeafletDirectiveWrapper } from '@bluehalo/ngx-leaflet';
import { of } from 'rxjs';
import type { Event } from '../events/shared/event';
import { LocationHelpers } from 'src/app/shared/generic/location';
import type { OnChanges, SimpleChanges } from '@angular/core';
import { Directive, Inject, Input } from '@angular/core';
import type { UserPrefs } from 'src/app/user/user-preferences/shared/user-prefs';

@Directive({
  selector: '[mtxEventLayer]',
  standalone: false,
})
export class EventLayerDirective implements OnChanges {

  @Input()
  public mtxEventLayer: Event[] = [];

  @Input()
  public mtxUserPrefs?: UserPrefs;

  public leafletDirectiveWrapper: LeafletDirectiveWrapper;

  public eventMarkers: L.LayerGroup = new L.LayerGroup([]);

  private readonly icons = {
    idle: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/idling.png' }),
    stopped: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/stopped.png' }),
    dvrOn: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/key-on.png' }),
    overSpeed: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/overspeed.png' }),
    stopArmOn: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/sa-on.png' }),
    stopArmOff: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/sa-off.png' }),
    amberLightOn: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/amber-light-on.png' }),
    geoFencing: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/geo-fencing.png' }),
    panic: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/panic.png' }),
    keyOff: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/key-off.png' }),
    stopArmViolation: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/sav.png' }),
    // breadcrumb: new L.Icon({ iconSize: [4, 4], iconAnchor: [2, 2], iconUrl: '/assets/images/icons/breadcrumb.png' }),
    mechanicalArm: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/mechanical-arm.png' }),
    unknown: new L.Icon({ iconSize: [20, 20], iconAnchor: [10, 10], iconUrl: '/assets/images/icons/unknown.png' }),
  };

  public constructor(@Inject(LeafletDirective) leafletDirective: LeafletDirective) {
    this.leafletDirectiveWrapper = new LeafletDirectiveWrapper(leafletDirective);

    /*
     * todo: do a PR to change this from EventEmitter so we dont have to subscribe so early.
     * this also may not be early enough where the real issue could come in..
     */
    const trigger = leafletDirective.getMap() ? of(leafletDirective.getMap()) : leafletDirective.mapReady;
    trigger.subscribe(map => {
      this.eventMarkers.addTo(map);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.mtxEventLayer) {
      this.applyBoundEvents();
    }
  }

  public fitToBounds(): void {
    const markers: L.Marker[] = this.eventMarkers
      .getLayers()
      .filter(layer => layer instanceof L.Marker);
    const points: [number, number][] = markers.map(marker => [
      marker.getLatLng().lat,
      marker.getLatLng().lng,
    ]);

    const bounds = new L.LatLngBounds(points);

    if (bounds.isValid()) {
      this.leafletDirectiveWrapper.getMap()?.fitBounds(bounds);
    }
  }

  public findEvent(evt: Event): void {
    if (!evt) {
      return;
    }

    let found = false;
    this.eventMarkers.getLayers().forEach((marker: L.Layer) => {
      if (!(marker instanceof L.Marker)) {
        return;
      }

      const markerMtxEvtId = (marker as any).mtxEventId;

      if (Boolean(markerMtxEvtId) && markerMtxEvtId === evt.id) {
        this.leafletDirectiveWrapper?.getMap()?.setView(marker.getLatLng(), 17);
        marker.openPopup();
        found = true;
      } else if (marker.isPopupOpen()) {
        marker.closePopup();
      }
    });

    // missing id fallback, go to where should be
    if (!found && LocationHelpers.HasGeometry(evt.location)) {
      this.leafletDirectiveWrapper
        ?.getMap()
        ?.setView([
          evt.location!.geometry!.coordinates[1],
          evt.location!.geometry!.coordinates[0],
        ],
        17);
    }
  }

  private applyBoundEvents(): void {
    this.eventMarkers?.clearLayers();

    this.mtxEventLayer?.forEach((evt: Event) => {
      const marker = this.buildMarker(evt);
      marker?.addTo(this.eventMarkers);
    });
  }


  private buildMarker(evt: Event): L.Marker | null {
    if (!evt || !LocationHelpers.HasGeometry(evt.location)) {
      return null;
    }

    const loc: [number, number] = [
      evt.location!.geometry!.coordinates[1],
      evt.location!.geometry!.coordinates[0],
    ];
    const marker = new L.Marker(loc);

    let geomDesc = '';
    let addrDesc = '';

    if (LocationHelpers.HasGeometry(evt.location)) {
      geomDesc = `${evt.location!.geometry!.coordinates[1]}, ${evt.location!.geometry!.coordinates[0]}`;
    }

    if (LocationHelpers.HasAddress(evt.location)) {
      addrDesc = LocationHelpers.SingleLineAddress(evt.location);
    }

    const contentBlock = document.createElement('div');
    const lineBreak = document.createElement('br');
    contentBlock.id = 'window';
    if (evt.vehicle) {
      const b1 = document.createElement('b');
      b1.textContent = evt.vehicle.name;
      contentBlock.append(b1);
      contentBlock.append(lineBreak.cloneNode());
    }
    const b2 = document.createElement('b');
    b2.textContent = 'Date: ';
    const b3 = document.createElement('b');
    b3.textContent = 'Time: ';
    const b4 = document.createElement('b');
    b4.textContent = 'Event: ';
    const b5 = document.createElement('b');
    b5.textContent = 'Speed: ';
    const b6 = document.createElement('b');
    b6.textContent = 'Heading: ';
    const b7 = document.createElement('b');
    b7.textContent = 'Location: ';
    const b8 = document.createElement('b');
    b8.textContent = 'Address: ';

    contentBlock.append(b2);
    contentBlock.append(moment(evt.date_time, 'YYYY-MM-DDTHH:mm:ss').format(this.getDateFormatToUse()));
    contentBlock.append(lineBreak.cloneNode());

    contentBlock.append(b3);
    contentBlock.append(moment(evt.date_time, 'YYYY-MM-DDTHH:mm:ss').format(this.getTimeFormatToUse()));
    contentBlock.append(lineBreak.cloneNode());

    contentBlock.append(b4);
    contentBlock.append(evt.description);
    contentBlock.append(lineBreak.cloneNode());

    contentBlock.append(b5);
    contentBlock.append(`${evt.speed_mph} mph`);
    contentBlock.append(lineBreak.cloneNode());

    contentBlock.append(b6);
    contentBlock.append(evt.heading ? `${evt.heading}°` : '-');
    contentBlock.append(lineBreak.cloneNode());

    contentBlock.append(b7);
    contentBlock.append(geomDesc);
    contentBlock.append(lineBreak.cloneNode());

    contentBlock.append(b8);
    contentBlock.append(addrDesc);
    contentBlock.append(lineBreak.cloneNode());

    const icon = this.getIcon(evt);

    if (icon) {
      marker.setIcon(icon);
    }

    // todo: adjust popup offset
    marker.bindPopup(contentBlock, {
      className: 'map-vehicle-popup',
      autoClose: false,
      autoPan: true,
      keepInView: false,
      offset: [
        -2.5,
        -15,
      ],
    });

    // some events don't have ids :(
    (marker as any).mtxEventId = evt.id;

    return marker;
  }


  private getIcon(evt: Event): L.Icon {
    if (evt.type === 'stopped') {
      return this.icons.stopped;
    }

    if (evt.type === 'idle') {
      return this.icons.idle;
    }

    if (evt.type === 'key_on') {
      return this.icons.dvrOn;
    }

    if (evt.type === 'stop_arm') {
      if (evt.details?.state === 'ON') {
        return this.icons.stopArmOn;
      }
      if (evt.details?.state === 'OFF') {
        return this.icons.stopArmOff;
      }
    }

    if (evt.type === 'panic_event') {
      return this.icons.panic;
    }

    if (evt.type === 'key_off') {
      return this.icons.keyOff;
    }

    if (evt.type === 'geo_fencing') {
      return this.icons.geoFencing;
    }

    if (evt.type === 'mechanical_arm') {
      return this.icons.mechanicalArm;
    }

    if (evt.type === 'amber_light') {
      if (evt.details?.state === 'ON') {
        return this.icons.amberLightOn;
      }
    }

    return this.icons.unknown;
  }

  private getTimeFormatToUse(): string {
    const formatToUse = this.mtxUserPrefs?.use_24_hour_clock === true ? 'HH:mm:ss' : 'hh:mm:ss A';
    return formatToUse;
  }

  private getDateFormatToUse(): string {
    return this.mtxUserPrefs?.date_format ?? 'MM/DD/YYYY';
  }

}
